import { Box, Container, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useTheme, Chip } from '@mui/material';
import { Helmet } from 'react-helmet';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import aeograph from 'assets/images/blog/aeograph.png';
import gptWiki from 'assets/images/blog/GPT_WIKI.png';
import perplexity from 'assets/images/blog/PERPLEXITY.png';
import copilot from 'assets/images/blog/COPILOT.png';
import trafficCitations from 'assets/images/blog/TRAFIC_CITATATIONS.png';
import backlinksCitations from 'assets/images/blog/BACKLINKS_CITATATIONS.png';
import listicles from 'assets/images/blog/LISTICLES.png';
import recencyBias from 'assets/images/blog/RECENCY_BIAS.png';
import CTASection from 'components/marketing/CTASection';
import Header from '../../../layout/CommonLayout/Header';

// ==============================|| AEO ARTICLE ||============================== //

const AEOArticle = () => {
  const theme = useTheme();

  const articleData = {
    title: 'What is AEO? A Deep Dive into Answer Engine Optimization',
    date: 'June 7, 2025',
    author: 'Vlad Rimo',
    authorUrl: 'https://www.linkedin.com/in/vlad-rimo/',
    publisher: 'Askpot',
    publisherLogo: 'https://askpot.com/favicon.svg',
    description: "Analysis of Answer Engine Optimization (AEO) based on Josh Blyskal's talk at BrightonSEO. Learn why AEO is not SEO and discover actionable strategies to win in AI search.",
    image: aeograph,
    category: 'Industry Insights'
  };

  const searchComparisonData = [
    { comparison: 'ChatGPT vs. Google SERP', percentage: '12%' },
    { comparison: 'ChatGPT vs. Bing SERP', percentage: '26%' }
  ];

  const backlinkData = [
    { backlinkCount: 'Sites with 1-9 backlinks', citations: '2,160 Citations (avg)' },
    { backlinkCount: 'Sites with 10+ backlinks', citations: '681 Citations (avg)' }
  ];

  const faqData = [
    {
      question: 'What is Answer Engine Optimization (AEO)?',
      answer: 'Answer Engine Optimization (AEO) is the practice of optimizing digital content to be found, understood, and cited by AI-powered answer engines like ChatGPT, Perplexity, and Google AI Overviews. It focuses on providing direct, easily parsable information to the AI.'
    },
    {
      question: 'How is AEO different from SEO?',
      answer: 'AEO is different from SEO because it targets the AI as the primary audience, not the end-user. While SEO focuses on ranking signals like backlinks and domain authority to earn clicks, AEO focuses on signals like content structure, recency, and semantic clarity to earn citations within an AI-generated answer.'
    },
    {
      question: 'Do backlinks matter for AEO?',
      answer: 'Research shows that backlinks have a very weak, and sometimes even inverse, correlation with AI citations. Sites with fewer, highly relevant backlinks are often cited more frequently than sites with a high volume of backlinks. AEO prioritizes content quality and structure over traditional backlink authority.'
    },
    {
      question: 'What is the best content format for AEO?',
      answer: 'Listicles and comparative content are the most dominant formats in AI search. Because AI is &apos;lazy,&apos; it prefers content that is clearly structured, easy to parse, and directly compares options, making it simple to synthesize an answer for the user.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>{articleData.title}</title>
        <meta name="description" content={articleData.description} />
        <meta property="og:title" content={articleData.title} />
        <meta property="og:description" content={articleData.description} />
        <meta property="og:image" content={articleData.image} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={articleData.title} />
        <meta name="twitter:description" content={articleData.description} />
        <meta name="twitter:image" content={articleData.image} />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': 'https://www.askpot.com/blog/answer-engine-optimization-aeo'
            },
            headline: articleData.title,
            description: articleData.description,
            image: articleData.image,
            author: {
              '@type': 'Person',
              name: articleData.author,
              url: articleData.authorUrl
            },
            publisher: {
              '@type': 'Organization',
              name: articleData.publisher,
              logo: {
                '@type': 'ImageObject',
                url: articleData.publisherLogo
              }
            },
            datePublished: '2025-06-07',
            dateModified: '2025-06-07'
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqData.map(faq => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer
              }
            }))
          })}
        </script>
      </Helmet>

      <Box component="main" sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <Header />
        
        {/* Hero Section */}
        <Box
          sx={{
            bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
            py: { xs: 8, md: 12 }
          }}
        >
          <Container maxWidth="md" sx={{ px: 4 }}>
            <Chip 
              label={articleData.category} 
              color="primary" 
              sx={{ mb: 3 }}
            />
            <Typography 
              variant="h1" 
              sx={{ 
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 700,
                letterSpacing: '-0.02em',
                mb: 3
              }}
            >
              {articleData.title}
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 2, 
              mb: 3,
              color: 'text.secondary'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarTodayIcon fontSize="small" />
                <Typography>{articleData.date}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon fontSize="small" />
                <Typography 
                  component="a" 
                  href={articleData.authorUrl}
                  sx={{ 
                    color: 'inherit',
                    textDecoration: 'none',
                    '&:hover': { color: 'primary.main' }
                  }}
                >
                  {articleData.author}
                </Typography>
              </Box>
            </Box>
            <Typography 
              variant="h4" 
              sx={{ 
                color: 'text.secondary',
                fontSize: { xs: '1.25rem', md: '1.5rem' }
              }}
            >
                
              {articleData.description}
            </Typography>
          </Container>
        </Box>

        {/* Featured Image */}
        <Container maxWidth="md" sx={{ px: 4, mt: -4 }}>
          <Box
            component="img"
            src={aeograph}
            alt="ChatGPT results overlap 12% with the Google SERP"
            sx={{
              width: '100%',
              maxWidth: 1000,
              display: 'block',
              mx: 'auto',
              borderRadius: 2
            }}
          />
          <Typography variant="caption" sx={{ mt: 1, color: 'text.secondary', textAlign: 'center', display: 'block' }}>
            Figure: ChatGPT results overlap 12% with the Google SERP.<br/>
            Source: BrightonSEO presentation by Josh Blyskal (Profound)
          </Typography>
        </Container>

        {/* Content Section */}
        <Container maxWidth="md" sx={{ px: 4, py: 8 }}>
          <Box sx={{ 
            '& .MuiTypography-root': {
              fontSize: '1.125rem',
              lineHeight: 1.75
            }
          }}>
            {/* Introduction */}
            <Typography paragraph>
              The ground beneath the world of digital marketing is shifting. For years, Search Engine Optimization (SEO) has been the undisputed king of organic visibility. But as generative AI platforms like ChatGPT, Perplexity, and Google&apos;s AI Overviews become the first point of contact for millions of users, a new discipline is emerging: <strong>Answer Engine Optimization (AEO)</strong>.
            </Typography>

            <Typography paragraph>
              At a recent BrightonSEO conference, <strong>Josh Blyskal, AI Search Strategist at <a href="https://www.tryprofound.com/" target="_blank" rel="noopener noreferrer">Profound</a></strong>, delivered a groundbreaking presentation based on an analysis of <strong>over 41 million AI search results</strong> (a significant update from the 10 million in the talk&apos;s title). His findings reveal a startling disconnect between traditional search and AI search, offering a crucial playbook for marketers who want to stay ahead.
            </Typography>

            <Typography paragraph>
              This isn&apos;t just a new name for SEO. It&apos;s a fundamentally different game with a new set of rules.
            </Typography>

            {/* Market Shift */}
            <Typography 
              variant="h2" 
              sx={{ 
                fontSize: '2rem',
                fontWeight: 700,
                mt: 8,
                mb: 4
              }}
            >
              The Fundamental Shift: Why AI Search Isn&apos;t SEO
            </Typography>

            <Typography paragraph>
              The core difference between SEO and AEO lies in the user journey.
            </Typography>

            <Box component="ul" sx={{ pl: 4, mb: 4 }}>
              <Typography component="li" paragraph>
                <strong>The Old Paradigm (SEO):</strong> A user searches on Google, clicks a link, and lands on your website. The relationship is direct:
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center',
                  my: 2
                }}>
                  <code>User ↔ Website</code>
                </Box>
              </Typography>
              <Typography component="li" paragraph>
                <strong>The New Paradigm (AEO):</strong> A user asks a question to an AI. The AI scours the web, synthesizes information, and provides a direct answer, sometimes citing its sources. The AI now sits between you and your potential customer:
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center',
                  my: 2
                }}>
                  <code>User ↔ AI ↔ Website</code>
                </Box>
              </Typography>
            </Box>

            <Typography paragraph>
              As Blyskal puts it, &quot;AI now acts as both connector and arbitrator, owning the direct relationship with users.&quot; This AI middle-layer changes everything. It decides what&apos;s true, what&apos;s relevant, and which brands even get mentioned.
            </Typography>

            <Typography paragraph>
              The data supports this seismic shift. In the last six months, Google&apos;s search market share has seen its first decrease in a decade, while ChatGPT&apos;s has skyrocketed by over 400%. The audience is moving, and our strategies must move with them.
            </Typography>

            {/* NEW SECTION: A Startling Disconnect */}
            <Typography 
              variant="h2" 
              sx={{ 
                fontSize: '2rem',
                fontWeight: 700,
                mt: 8,
                mb: 4
              }}
            >
              A Startling Disconnect: Why Traditional SEO Fails in AI Search
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
              <Box
                component="img"
                src={aeograph}
                alt="ChatGPT results overlap 12% with the Google SERP"
                sx={{
                  maxWidth: '100%',
                  width: 1000,
                  borderRadius: 2
                }}
              />
              <Typography variant="caption" sx={{ mt: 1, color: 'text.secondary', textAlign: 'center', display: 'block' }}>
                Figure: ChatGPT results overlap 12% with the Google SERP.<br/>
                Source: BrightonSEO presentation by Josh Blyskal (Profound)
              </Typography>
            </Box>

            <Paper sx={{ mb: 6, overflow: 'hidden' }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Comparison</TableCell>
                      <TableCell align="right">Percentage of Overlapping Results</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {searchComparisonData.map((row) => (
                      <TableRow key={row.comparison}>
                        <TableCell sx={{ fontWeight: 500 }}>{row.comparison}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{row.percentage}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            <Typography variant="h3" sx={{ mt: 6, mb: 2, fontWeight: 600 }}>
              Traffic ≠ AI Citations
            </Typography>
            <Typography paragraph>
              A site&apos;s popularity with users does not equal authority with AI. Profound&apos;s analysis shows that a site&apos;s monthly traffic has almost no correlation with the number of AI citations it receives. The r² value of 0.05 means that <strong>95% of AI citation behavior cannot be explained by traffic.</strong>
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
              <Box component="img" src={trafficCitations} alt="Scatter plot: Traffic vs. AI Citations" sx={{ maxWidth: '100%', width: 1000, borderRadius: 2 }} />
              <Typography variant="caption" sx={{ mt: 1, color: 'text.secondary', textAlign: 'center', display: 'block', fontSize: '0.65rem' }}>
                Figure: Scatter plot showing no correlation between traffic and AI citations.<br/>
                Source: BrightonSEO presentation by Josh Blyskal (Profound)
              </Typography>
            </Box>

            <Typography variant="h3" sx={{ mt: 6, mb: 2, fontWeight: 600 }}>
              Backlinks ≠ AI Citations
            </Typography>
            <Typography paragraph>
              Even more shocking is the role of backlinks. The data shows that <strong>97.2% of AI citation behavior cannot be explained by backlinks.</strong> The relationship is not just weak; it&apos;s often inverse. This suggests that the sheer volume of links, a cornerstone of SEO, is not a signal of trust for answer engines.
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 4 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Backlink Count</TableCell>
                      <TableCell align="right">Average AI Citations Received</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {backlinkData.map((row) => (
                      <TableRow key={row.backlinkCount}>
                        <TableCell component="th" scope="row">
                          <strong>{row.backlinkCount}</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>{row.citations}</strong>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
              <Box component="img" src={backlinksCitations} alt="Scatter plot: Backlinks vs. AI Citations" sx={{ maxWidth: '100%', width: 1000, borderRadius: 2 }} />
              <Typography variant="caption" sx={{ mt: 1, color: 'text.secondary', textAlign: 'center', display: 'block', fontSize: '0.65rem' }}>
                Figure: Data shows sites with fewer backlinks often receive significantly more AI citations.<br/>
                Source: BrightonSEO presentation by Josh Blyskal (Profound)
              </Typography>
            </Box>

            {/* The Biases of AI */}
            <Typography variant="h2" sx={{ mt: 8, mb: 3, fontWeight: 700 }}>
              The Biases of AI: What Each Engine Prefers
            </Typography>
            <Typography variant="h3" sx={{ mt: 6, mb: 2, fontWeight: 600 }}>
              ChatGPT Loves Wikipedia
            </Typography>
            <Typography paragraph>
              When analyzing the most-cited domains within ChatGPT, one source stands above all others: <strong>Wikipedia</strong>. According to Blyskal&apos;s data, it is cited &quot;by a factor of six compared to anything else.&quot; This demonstrates a powerful bias towards established, encyclopedic, and neutrally-toned content. While other sites like G2, Forbes, and Amazon appear, Wikipedia&apos;s dominance is the key takeaway for anyone targeting ChatGPT.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
              <Box component="img" src={gptWiki} alt="Bar chart: ChatGPT most-cited domains (Wikipedia dominates)" sx={{ maxWidth: '100%', width: 1000, borderRadius: 2 }} />
              <Typography variant="caption" sx={{ mt: 1, color: 'text.secondary', textAlign: 'center', display: 'block', fontSize: '0.65rem' }}>
                Figure: ChatGPT most-cited domains (Wikipedia dominates).<br/>
                Source: BrightonSEO presentation by Josh Blyskal (Profound)
              </Typography>
            </Box>

            <Typography variant="h3" sx={{ mt: 6, mb: 2, fontWeight: 600 }}>
              Perplexity is UGC-Focused
            </Typography>
            <Typography paragraph>
              Perplexity, on the other hand, shows a clear preference for the voice of the people. Its most-cited domain is <strong>Reddit</strong>, with a staggering 3.2 million citations in the dataset. This is followed by other user-generated content (UGC) and social platforms like YouTube and LinkedIn. This indicates that for Perplexity, authentic conversations and community-driven answers are weighted heavily.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
              <Box component="img" src={perplexity} alt="Bar chart: Perplexity most-cited domains (Reddit dominates)" sx={{ maxWidth: '100%', width: 1000, borderRadius: 2 }} />
              <Typography variant="caption" sx={{ mt: 1, color: 'text.secondary', textAlign: 'center', display: 'block', fontSize: '0.65rem' }}>
                Figure: Perplexity most-cited domains (Reddit dominates).<br/>
                Source: BrightonSEO presentation by Josh Blyskal (Profound)
              </Typography>
            </Box>

            <Typography variant="h3" sx={{ mt: 6, mb: 2, fontWeight: 600 }}>
              Copilot Gravitates Towards Forbes—Massively
            </Typography>
            <Typography paragraph>
              Microsoft&apos;s Copilot carves its own path, showing a massive preference for established business and tech publications. <strong>Forbes</strong> and <strong>Gartner</strong> are the top two sources by a significant margin. This suggests Copilot&apos;s algorithm is trained to trust a narrower set of what it considers highly authoritative commercial and B2B sources.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
              <Box component="img" src={copilot} alt="Bar chart: Copilot most-cited domains (Forbes, Gartner)" sx={{ maxWidth: '100%', width: 1000, borderRadius: 2 }} />
              <Typography variant="caption" sx={{ mt: 1, color: 'text.secondary', textAlign: 'center', display: 'block', fontSize: '0.65rem' }}>
                Figure: Copilot most-cited domains (Forbes, Gartner).<br/>
                Source: BrightonSEO presentation by Josh Blyskal (Profound)
              </Typography>
            </Box>

            {/* AEO Playbook */}
            <Box sx={{ my: 6 }}>
              <Typography variant="h4" gutterBottom>
                The AEO Playbook: How to Win in the New World of Search
              </Typography>
              
              <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
                Part 1: The Technical Foundation - Speaking the AI&apos;s Language
              </Typography>
              
              <Box component="ol" sx={{ pl: 4 }}>
                <Typography component="li" variant="body1" paragraph>
                  <strong>Understand the WebGPT Model:</strong> This is a crucial, often-missed detail. When ChatGPT browses the web, it uses a specialized model called <strong>WebGPT</strong> to be the final decision-maker. This model was trained specifically to look at search results and determine which ones have the most utility. It primarily sees only three things from the SERP: the <strong>URL</strong>, the <strong>Title Tag</strong>, and the <strong>Meta Description</strong>. Your optimization efforts must be laser-focused on these three elements.
                </Typography>

                <Typography component="li" variant="body1" paragraph>
                  <strong>Get Indexed by Bing:</strong> This is the absolute bedrock of AEO for ChatGPT. The process follows a clear hierarchy: if you&apos;re not in the Bing Index, you won&apos;t be in the ChatGPT Index, your content won&apos;t be crawled by WebGPT, you won&apos;t be cited, and you will never appear in an answer. <strong>Welcome to caring about Bing Webmaster Tools.</strong>
                </Typography>

                <Typography component="li" variant="body1" paragraph>
                  <strong>AI Crawlers Don&apos;t Interact with JavaScript:</strong> Answer Engine crawlers are not as sophisticated as Google&apos;s. They primarily parse the raw HTML. If your key content is rendered client-side via JavaScript, the AI crawler will likely miss it. Ensure your most important information is present in the initial HTML payload.
                </Typography>

                <Typography component="li" variant="body1" paragraph>
                  <strong>Master llms.txt:</strong> This is your new best friend. Similar to robots.txt, llms.txt is a new protocol that allows you to give direct instructions to AI crawlers. You can define key pieces of content, point to specific links, and structure your information in a way that is perfectly digestible for an LLM. It&apos;s a powerful way to control how your brand is represented.
                </Typography>

                <Typography component="li" variant="body1" paragraph>
                  <strong>Semantic URL Slugs Win:</strong> Since the URL is one of the three key pieces of information WebGPT sees, it must be descriptive.
                  <ul className="space-y-2 my-4 pl-5 list-disc">
                    <li>
                      <strong>Bad:</strong> <code>goodonyou.eco/abd7t-ethical-9z3k-brands-uk/</code>
                    </li>
                    <li>
                      <strong>Good:</strong> <code>goodonyou.eco/most-ethical-and-sustainable-clothing-brands-from-uk/</code>
                    </li>
                  </ul>
                  The URL itself becomes a powerful signal of relevance.
                </Typography>

                <Typography component="li" variant="body1" paragraph>
                  <strong>Spoil Your Content in the Meta Description:</strong> This is counterintuitive to traditional SEO, where the goal is to entice a click. For AEO, you want to provide the answer directly. WebGPT is looking for the most confident and direct answer.
                  <Box component="ul" sx={{ pl: 4, mt: 2 }}>
                    <Typography component="li" variant="body1">
                      <strong>Example Meta Description:</strong>
                      <Box component="pre" sx={{ 
                        p: 2, 
                        bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
                        borderRadius: 1,
                        mt: 1,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}>
                        <code>
                          {`<meta 
  name="description" 
  content="The best business credit cards include Ink Business Unlimited, 
  Venture X Business and U.S. Bank Business Triple Cash Rewards, 
  among other options."
>`}
                        </code>
                      </Box>
                    </Typography>
                  </Box>
                </Typography>
              </Box>

              <Typography variant="h5" gutterBottom sx={{ mt: 6 }}>
                Part 2: The Content Strategy - Feeding the Lazy AI
              </Typography>

              <Typography variant="h3" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
                Create &lsquo;Lazy&rsquo; Content: Prioritize Listicles and Comparisons
              </Typography>
              <Typography paragraph>
                As Blyskal memorably stated, &quot;AI Search is lazy.&quot; It wants information that is easy to parse, compare, and synthesize. This is why <strong>Comparative Listicles</strong> are the winning format, accounting for a massive <strong>32.5% of all citations</strong> in their 177 million source dataset. This is more than 3x the next closest category (Blogs/Opinion at 9.91%).
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
                <Box component="img" src={listicles} alt="Bar chart: Listicle format dominates AI citations" sx={{ maxWidth: '100%', width: 1000, borderRadius: 2 }} />
                <Typography variant="caption" sx={{ mt: 1, color: 'text.secondary', textAlign: 'center', display: 'block', fontSize: '0.65rem' }}>
                  Figure: Listicle format dominates AI citations.<br/>
                  Source: BrightonSEO presentation by Josh Blyskal (Profound)
                </Typography>
              </Box>

              <Typography variant="h3" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
                Leverage Recency Bias
              </Typography>
              <Typography paragraph>
                Answer Engines have a strong bias towards fresh content. In one case study, a client in the cybersecurity space was knocked from the #1 cited position simply because their competitors had updated their content more recently. After a simple refresh (updating the year to 2025 and tweaking a few points), they shot back to the top spot within a week.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
                <Box component="img" src={recencyBias} alt="Screenshot: Recency bias in top-ranking articles" sx={{ maxWidth: '100%', width: 1000, borderRadius: 2 }} />
                <Typography variant="caption" sx={{ mt: 1, color: 'text.secondary', textAlign: 'center', display: 'block', fontSize: '0.65rem' }}>
                  Figure: Recency bias in top-ranking articles.<br/>
                  Source: BrightonSEO presentation by Josh Blyskal (Profound)
                </Typography>
              </Box>

              <Box component="ol" sx={{ pl: 4 }}>
                <Typography component="li" variant="body1" paragraph>
                  <strong>Embrace UGC and Situational Social Media:</strong> Platforms like Perplexity are heavily focused on user-generated content (UGC). <strong>Reddit is the single most-cited domain on Perplexity</strong>, followed by YouTube and LinkedIn. Engaging in relevant community discussions on these platforms is no longer just a social media strategy; it&apos;s a core AEO tactic.
                </Typography>
              </Box>
            </Box>

            {/* Conclusion */}
            <Typography 
              variant="h2" 
              sx={{ 
                fontSize: '2rem',
                fontWeight: 700,
                mt: 8,
                mb: 4
              }}
            >
              The Future is Now: A Prime Opportunity
            </Typography>

            <Typography paragraph>
              As a former SEO, Blyskal acknowledges that the discipline has sometimes been seen as the &quot;black sheep of marketing.&quot; But he concludes with a powerful prediction:
            </Typography>

            <Box sx={{ 
              borderLeft: 4, 
              borderColor: 'primary.main', 
              pl: 3, 
              py: 2, 
              my: 4,
              bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
              fontStyle: 'italic'
            }}>
              <Typography variant="h5">
                &quot;AI search is about to become the sexiest area of digital marketing, and everyone in this room has a prime opportunity.&quot;
              </Typography>
            </Box>

            <Typography paragraph>
              The old playbooks are becoming obsolete, and a new frontier is opening up. The brands and marketers who succeed will be those who recognize this shift and start building their AEO capabilities today.
            </Typography>

            <Typography paragraph>
              Start tracking key queries. Analyze the sources AI is citing in your industry. Experiment with content formats and technical optimizations. The opportunity to become a dominant voice in this new landscape is immense, and it belongs to those who are willing to learn and adapt.
            </Typography>

            {/* Video Section */}
            <Typography 
              variant="h2" 
              sx={{ 
                fontSize: '2rem',
                fontWeight: 700,
                mt: 8,
                mb: 4
              }}
            >
              Watch the Original Presentation
            </Typography>
            <Box sx={{ 
              position: 'relative',
              paddingTop: '56.25%',
              width: '100%',
              bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
              borderRadius: 2,
              overflow: 'hidden',
              mb: 6
            }}>
              <iframe
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 0
                }}
                src="https://www.youtube.com/embed/slE1sgPReTM"
                title="Beyond SEO: A Deep Dive into Answer Engine Optimization (AEO)"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </Box>

            {/* FAQ Section */}
            <Typography 
              variant="h2" 
              sx={{ 
                fontSize: '2rem',
                fontWeight: 700,
                mt: 8,
                mb: 4
              }}
            >
              Frequently Asked Questions
            </Typography>
            <Box sx={{ mb: 6 }}>
              {faqData.map((faq, index) => (
                <Box key={index} sx={{ mb: 4 }}>
                  <Typography 
                    variant="h3" 
                    sx={{ 
                      fontSize: '1.25rem',
                      fontWeight: 600,
                      mb: 2
                    }}
                  >
                    {faq.question}
                  </Typography>
                  <Typography color="text.secondary">
                    {faq.answer}
                  </Typography>
                  {index < faqData.length - 1 && (
                    <Box 
                      sx={{ 
                        height: 1,
                        bgcolor: 'divider',
                        my: 3
                      }} 
                    />
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        </Container>

        <CTASection />
      </Box>
    </>
  );
};

export default AEOArticle; 